/**
 * GroupInvitationModal - Two tabs: Friends (add to group) and Invite Link (Coming Soon).
 * Uses BaseModal; Friends tab wraps FriendSelectorList; Add calls addGroupMembers; parent onSuccess receives invited IDs for optimistic update.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Pressable, Text, View, ActivityIndicator, Platform } from 'react-native'
import { X } from 'lucide-react-native'
import { useRouter } from 'expo-router'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { BaseModal } from '../../ui/BaseModal'
import { useTheme } from '../../../src/theme/useTheme'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { useSnackbar } from '../../../src/shared/hooks/useSnackbar'
import { useDialog } from '../../../src/shared/hooks/useDialog'
import { useShareLink } from '../../../src/shared/hooks/useShareLink'
import { addGroupMembers } from '../../../src/friends/helpers/groupMembersApi'
import { extractErrorMessage } from '../../../helpers/apiClient.helper'
import { getGroupInvitationModalStyles } from './GroupInvitationModal.styles'
import { FriendsTabContent } from './FriendsTabContent'
import { LinkTabContent } from './LinkTabContent'
import { DEEP_LINK_TYPES } from '../../../src/shared/types/deepLink.types'
import type { Group } from '../../../src/friends/types/groups.types'
import { INVITATION_MODAL_TABS, type InvitationModalTab } from './GroupInvitationModal.constants'

interface GroupInvitationModalProps {
  visible: boolean
  group: Group | null
  onClose: () => void
  /** Called with invited user IDs so parent can optimistically add them to pending (no refetch). */
  onSuccess: (invitedUserIds: string[]) => void | Promise<void>
  /** When true, skip API call and simulate success (for Storybook). */
  skipApiForStory?: boolean
}

export const GroupInvitationModal: React.FC<GroupInvitationModalProps> = ({
  visible,
  group,
  onClose,
  onSuccess,
  skipApiForStory = false,
}) => {
  const theme = useTheme()
  const styles = useMemo(() => getGroupInvitationModalStyles(theme), [theme])
  const apiClient = useApiClient()
  const { showSnackbar } = useSnackbar()
  const { showDialog } = useDialog()
  const router = useRouter()

  const [activeTab, setActiveTab] = useState<InvitationModalTab>(INVITATION_MODAL_TABS.FRIENDS)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [adding, setAdding] = useState(false)

  const {
    url: linkUrl,
    loading: linkLoading,
    error: linkError,
    generateLink,
    shareLink,
    copyLink,
    resetLink,
  } = useShareLink({
    type: DEEP_LINK_TYPES.GROUP,
    targetId: group?.id ?? undefined,
  })

  useEffect(() => {
    if (!visible) resetLink()
  }, [visible, resetLink])

  useEffect(() => {
    if (
      visible &&
      activeTab === INVITATION_MODAL_TABS.LINK &&
      linkUrl === null &&
      !linkLoading
    )
      generateLink()
  }, [visible, activeTab, linkUrl, linkLoading, generateLink])

  // Compute cap state before handleAdd so it's available in the closure
  const memberCount = group?.memberIds?.length ?? 0
  const cap = group?.memberCap ?? null
  const atMemberCap = cap !== null && memberCount >= cap

  const handleToggle = useCallback((friendId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(friendId)) next.delete(friendId)
      else next.add(friendId)
      return next
    })
  }, [])

  const handleAdd = useCallback(async () => {
    if (!group) return

    if (atMemberCap) {
      const confirmed = await showDialog({
        title: 'Group is full',
        message: cap !== null
          ? `This group has ${memberCount}/${cap} members. Remove existing members to free up space, or upgrade to Pro for up to 100 members.`
          : 'This group has reached its member limit.',
        confirmText: 'Upgrade to Pro',
        cancelText: 'Remove Members',
      })
      if (confirmed) {
        onClose()
        router.push('/packs')
      } else {
        onClose()
      }
      return
    }

    const userIds = Array.from(selectedIds).filter(
      (id): id is string => typeof id === 'string' && id.length > 0
    )
    if (userIds.length === 0) return
    setAdding(true)
    try {
      if (skipApiForStory) {
        setSelectedIds(new Set())
        onSuccess(userIds)
        onClose()
        return
      }
      await addGroupMembers(group.id, userIds, apiClient)
      setSelectedIds(new Set())
      await onSuccess(userIds)
      showSnackbar('Members added to the group.', 'success')
      onClose()
    } catch (err) {
      const message = extractErrorMessage(err, 'Could not add members. Try again.')
      const isCapError =
        (err as { response?: { status?: number } })?.response?.status === 403 ||
        message.toLowerCase().includes('member limit')
      if (isCapError) {
        const confirmed = await showDialog({
          title: 'Group is full',
          message: cap !== null
            ? `This group has ${memberCount}/${cap} members. Remove existing members to free up space, or upgrade to Pro for up to 100 members.`
            : message,
          confirmText: 'Upgrade to Pro',
          cancelText: 'Remove Members',
        })
        if (confirmed) {
          onClose()
          router.push('/packs')
        } else {
          onClose()
        }
        return
      }
      showSnackbar(message, 'error')
    } finally {
      setAdding(false)
    }
  }, [group, atMemberCap, memberCount, cap, showDialog, router, onClose, selectedIds, apiClient, onSuccess, showSnackbar, skipApiForStory])

  if (!group) return null

  // At cap: button is always pressable to surface the manage dialog
  const canAdd = activeTab === INVITATION_MODAL_TABS.FRIENDS && !adding && (atMemberCap || selectedIds.size > 0)

  return (
    <BaseModal
      isVisible={visible}
      onDismiss={onClose}
      contentContainerStyle={styles.contentCard}
      testID="group-invitation-modal"
    >
      <View style={styles.header}>
        <Text style={styles.headerTitle} numberOfLines={1} testID="group-invitation-modal-title">
          Add to {group.name}
        </Text>
        <Pressable
          onPress={onClose}
          style={({ pressed, hovered }) => [
            styles.closeButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.closeButtonPressed,
            hovered && Platform.OS === 'web' && styles.closeButtonHover,
          ]}
          accessibilityLabel="Close"
        >
          <X size={24} color={theme.gray[600]} />
        </Pressable>
      </View>

      <View style={styles.tabRow}>
        <Pressable
          style={({ pressed, hovered }) => [
            styles.tab,
            activeTab === INVITATION_MODAL_TABS.FRIENDS && styles.tabActive,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.tabPressed,
            hovered && Platform.OS === 'web' && styles.tabHover,
          ]}
          onPress={() => setActiveTab(INVITATION_MODAL_TABS.FRIENDS)}
        >
          <Text style={[styles.tabText, activeTab === INVITATION_MODAL_TABS.FRIENDS && styles.tabTextActive]}>
            {INVITATION_MODAL_TABS.FRIENDS}
          </Text>
        </Pressable>
        <Pressable
          style={({ pressed, hovered }) => [
            styles.tab,
            activeTab === INVITATION_MODAL_TABS.LINK && styles.tabActive,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.tabPressed,
            hovered && Platform.OS === 'web' && styles.tabHover,
          ]}
          onPress={() => setActiveTab(INVITATION_MODAL_TABS.LINK)}
        >
          <Text style={[styles.tabText, activeTab === INVITATION_MODAL_TABS.LINK && styles.tabTextActive]}>
            {INVITATION_MODAL_TABS.LINK}
          </Text>
        </Pressable>
      </View>

      <View style={styles.body}>
        <View style={activeTab !== INVITATION_MODAL_TABS.FRIENDS ? styles.tabPanelHidden : styles.tabPanelVisible}>
          <FriendsTabContent
            group={group}
            selectedIds={selectedIds}
            onToggle={handleToggle}
            styles={styles}
          />
        </View>
        <View style={activeTab !== INVITATION_MODAL_TABS.LINK ? styles.tabPanelHidden : styles.tabPanelVisible}>
          <LinkTabContent
            groupId={group.id}
            linkState={{
              url: linkUrl,
              loading: linkLoading,
              error: linkError,
              generateLink,
              shareLink,
              copyLink,
            }}
          />
        </View>
      </View>

      {activeTab === INVITATION_MODAL_TABS.FRIENDS && (
        <Pressable
          style={({ pressed, hovered }) => [
            styles.addButton,
            !canAdd && styles.addButtonDisabled,
            atMemberCap && canAdd && styles.addButtonAtCap,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            canAdd && pressed && styles.addButtonPressed,
            canAdd && hovered && Platform.OS === 'web' && styles.addButtonHover,
          ]}
          onPress={handleAdd}
          disabled={!canAdd}
          testID="group-invitation-modal-add-button"
        >
          {adding ? (
            <ActivityIndicator size="small" color={theme.white} />
          ) : (
            <Text style={styles.addButtonText}>
              {atMemberCap
                ? `Group is full (${memberCount}/${cap})`
                : `Add${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
            </Text>
          )}
        </Pressable>
      )}
    </BaseModal>
  )
}
